import axios from "axios";
import { jwtDecode } from "jwt-decode";

const API_URL = `${import.meta.env.VITE_API_URL}/users`;

// Types
export interface UserInfo {
  userId: string;
  name: string;
  email: string;
  role: string;
  token: string;
  refreshToken: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  isBlocked: boolean;
  orders: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UsersListResponse {
  users: User[];
  page: number;
  pages: number;
  total: number;
}

export interface UserSearchOptions {
  page: number;
  sortBy: string;
  sortOrder: string;
  search: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  phone: string;
  password: string;
}

export interface UpdateUserRequest {
  _id: string;
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
}

export interface ChangePasswordRequest {
  oldPass: string;
  newPass: string;
}

export interface AccountRequestData {
  name: string;
  email: string;
  phone: string;
}

// API functions
export const loginUser = async (credentials: LoginRequest): Promise<UserInfo> => {
  const { data } = await axios.post(`${API_URL}/login`, credentials);
  const decoded = jwtDecode<Omit<UserInfo, "token" | "refreshToken">>(data.token);
  return { ...decoded, token: data.token, refreshToken: data.refreshToken };
};

export const refreshAccessToken = async (
  refreshToken: string
): Promise<{ token: string; refreshToken: string }> => {
  const { data } = await axios.post(`${API_URL}/refresh`, { refreshToken });
  return { token: data.token, refreshToken: data.refreshToken };
};

export const fetchUsers = async (searchOptions: UserSearchOptions): Promise<UsersListResponse> => {
  const { data } = await axios.get(
    `${API_URL}?page=${searchOptions.page}&sortBy=${searchOptions.sortBy}&sortOrder=${searchOptions.sortOrder}&search=${searchOptions.search}`
  );
  return data;
};

export const fetchUser = async (id: string): Promise<User> => {
  const { data } = await axios.get(`${API_URL}/${id}`);
  return data;
};

export const createUser = async (user: CreateUserRequest): Promise<{ message: string }> => {
  const { data } = await axios.post(API_URL, user);
  return data;
};

export const updateUser = async (user: UpdateUserRequest): Promise<{ message: string }> => {
  const { data } = await axios.put(API_URL, user);
  return data;
};

export const updateAccountStatus = async (userId: string): Promise<{ message: string }> => {
  const { data } = await axios.post(`${API_URL}/blocking`, { userId });
  return data;
};

export const deleteUser = async (userId: string): Promise<{ message: string }> => {
  const { data } = await axios.delete(`${API_URL}/${userId}`);
  return data;
};

export const changePassword = async (
  passwords: ChangePasswordRequest
): Promise<{ message: string }> => {
  const { data } = await axios.post(`${API_URL}/change-pass`, passwords);
  return data;
};

export const requestAccount = async (
  userData: AccountRequestData
): Promise<{ message: string }> => {
  const { data } = await axios.post(`${API_URL}/account-request`, userData);
  return data;
};
